import { AccountDateTimePicker as DateTimePicker } from '../date-time-picker';
/**
 * Event editor (plan §3.1, T11 variant): title, calendar, start/end, all day, repeat, location with room picker,
 * required/optional attendees (RecipientInput with GAL suggestions), the scheduling strip with everybody's free/busy
 * for the day, show as, reminder, private and the description (the one rich-text editor). With attendees it is a
 * meeting: Save sends the invitations (updates for the organizer).
 */
import { useI18n } from '@platform/i18n';
import {
  Badge,
  BREAKPOINTS,
  Button,
  checkNames,
  CALENDAR_COLOR_CLASSES,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogBody,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Input,
  EntityListPicker,
  entityPickerLabelsFor,
  RecipientInput,
  RichTextEditor,
  SchedulingStrip,
  addDays,
  needsAttention,
  plainTextToMailHtml,
  recipientsFromInit,
  startOfDay,
  toast,
  useAsync,
  useErrorText,
  useDiscardGuard,
  useReportEdits,
  useMediaQuery,
  type Recipient,
  type SchedulingRow,
} from '@platform/ui';
import { BellIcon, BellOffIcon, CalendarCheckIcon, CalendarRangeIcon, ClockIcon, DoorOpenIcon, LockIcon, MapPinIcon, RepeatIcon, SaveIcon, SendIcon, TagIcon, Trash2Icon, UsersIcon, VideoIcon, XIcon, PaperclipIcon, TriangleAlertIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useBlocker } from 'react-router';
import { api, type CalendarEventInput, type Room } from '../api';
import { useAuth } from '../auth';
import { useRecipientLabels } from '../compose-labels';
import { useRecipientDirectory } from '../recipient-directory';
import { useCalendar } from './calendar-context';
import { nextHalfHour } from './zones';
import { calendarName, SHOW_AS_LABEL } from './events';
import { useCalendarFormat } from './format';
import { anchored, defaultRule, type Recurrence } from './recurrence';
import { RecurrenceEditor } from './recurrence-editor';
import { useUndoableCalendar } from './undo';
import { recentLocations, rememberLocation, useEditorAttachments } from './editor-attachments';
import { EventSecondaryMenu } from './event-secondary-menu';
import { RoomFinder } from './room-finder';

const pad = (n: number) => String(n).padStart(2, '0');
const dateValue = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeValue = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const combine = (date: string, time: string) => new Date(`${date}T${time || '00:00'}:00`);
const REMINDERS = [null, 0, 5, 10, 15, 30, 60, 120, 1440] as const;
/** http(s) links only (the join button opens it). */
const validUrl = (value: string) => /^https?:\/\/[^\s]+$/i.test(value);
const SHOW_AS = ['busy', 'tentative', 'free', 'oof', 'workingElsewhere'] as const;

export function EventEditor() {
  const { t } = useI18n();
  const isDesktop = useMediaQuery(BREAKPOINTS.md);
  const format = useCalendarFormat();
  const errorText = useErrorText();
  const { session } = useAuth();
  const { editor, closeEditor, calendars, changed, settings, categories, zone: display } = useCalendar();
  const undoable = useUndoableCalendar();
  const directory = useRecipientDirectory();
  const { recipientLabels } = useRecipientLabels();
  const ids = { locations: useId(), title: useId(), start: useId(), end: useId(), location: useId(), required: useId(), optional: useId(), error: useId() };
  const titleInput = useRef<HTMLInputElement>(null);

  const editing = editor?.mode === 'edit' ? editor.event : undefined;
  const scope = editor?.mode === 'edit' ? editor.scope : 'occurrence';
  const writable = (calendars.data ?? []).filter((c) => c.canCreate);
  const initialStart = editing ? display.toWall(editing.start) : (editor?.mode === 'create' && editor.start) || nextHalfHour(display);
  const created = editor?.mode === 'create' ? editor : undefined;
  const initialEnd = editing ? display.toWall(editing.end) : (created?.end ?? new Date(initialStart.getTime() + settings.defaultDurationMinutes * 60_000));

  const [calendarId, setCalendarId] = useState(editing?.calendarId ?? (editor?.mode === 'create' ? editor.calendarId : undefined) ?? 'own');
  const [subject, setSubject] = useState(editing?.subject ?? created?.title ?? '');
  const [allDay, setAllDay] = useState(editing?.allDay ?? (editor?.mode === 'create' && editor.allDay) ?? false);
  const [startDate, setStartDate] = useState(dateValue(initialStart));
  const [startTime, setStartTime] = useState(timeValue(initialStart));
  const [endDate, setEndDate] = useState(dateValue((editing?.allDay ?? created?.allDay) ? addDays(initialEnd, -1) : initialEnd));
  const [endTime, setEndTime] = useState(timeValue(initialEnd));
  const [location, setLocation] = useState(editing?.location ?? '');
  const people = editing?.attendees.filter((a) => a.address.toLowerCase() !== session?.username.toLowerCase()) ?? created?.attendees ?? [];
  const [required, setRequired] = useState<Recipient[]>(() => recipientsFromInit(people.filter((a) => a.type === 'required').map((a) => ({ email: a.address, name: a.name }))));
  const [optional, setOptional] = useState<Recipient[]>(() => recipientsFromInit(people.filter((a) => a.type === 'optional').map((a) => ({ email: a.address, name: a.name }))));
  const [rooms, setRooms] = useState<{ name: string; address: string }[]>(() => people.filter((a) => a.type === 'resource').map(({ name, address }) => ({ name, address })));
  const [showAs, setShowAs] = useState<(typeof SHOW_AS)[number]>(editing?.showAs ?? 'busy');
  // Several reminders: the smallest offset is the item's reminder (Outlook, phones), the others
  // are the platform's extra reminders (shown by the webmail).
  const [reminderSet, setReminderSet] = useState<number[]>(() =>
    [...new Set([...(editing ? [editing.reminderMinutes] : [settings.defaultReminderMinutes]), ...(editing?.reminders ?? [])].filter((m): m is number => m !== null))].sort((a, b) => a - b),
  );
  const reminder = reminderSet[0] ?? null;
  const [isPrivate, setPrivate] = useState(editing?.sensitivity === 'private');
  // A pattern only Outlook writes ("other") is shown but cannot be changed here.
  const readOnlyRule = editing?.recurrence?.pattern === 'other';
  // Recurrence is set on a new event, on a whole series, or to turn a single event into a series.
  const canRepeat = !editing || scope === 'series' || scope === 'following' || !editing.isRecurring;
  const [rule, setRule] = useState<Recurrence | null>(() => (created?.recurring ? defaultRule('weekly', initialStart) : (editing?.recurrence ?? null)));
  const [body, setBody] = useState<string>();
  const [chosenCategories, setChosenCategories] = useState<string[]>(editing?.categories ?? []);
  const [onlineUrl, setOnlineUrl] = useState(editing?.onlineMeetingUrl ?? '');
  // Advanced parts open from the bar (Outlook's ribbon); recurrence and scheduling show when in use.
  const [showRepeat, setShowRepeat] = useState(Boolean(created?.recurring));
  const [showOnline, setShowOnline] = useState(false);
  const [showScheduling, setShowScheduling] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [roomFinderOpen, setRoomFinderOpen] = useState(false);
  const reminderLabel = (m: number | null) =>
    m === null ? t('webmail.calendar.editor.noReminder') : m < 60 ? t('webmail.calendar.reminderMinutes', { n: m }) : t('webmail.calendarUx.reminders.before', { duration: format.duration(new Date(0), new Date(m * 60_000), false) });
  const remindersLabel = reminderSet.length > 1 ? t('webmail.calendarUx.reminders.several', { first: reminderLabel(reminder), n: reminderSet.length - 1 }) : reminderLabel(reminder);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const start = allDay ? startOfDay(combine(startDate, '00:00')) : combine(startDate, startTime);
  const end = allDay ? addDays(startOfDay(combine(endDate, '00:00')), 1) : combine(endDate, endTime);
  const slotValid = !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start;
  const valid = subject.trim() !== '' && slotValid;
  const roomList = useAsync(() => api.listRooms());
  const attachments = useEditorAttachments(editing);
  // Compare editable values with the first render, not defaults recalculated as the clock advances.
  // Recipient resolution status and expanded controls are presentation state, not event changes.
  const snapshot = JSON.stringify({ calendarId, subject, allDay, startDate, startTime, endDate, endTime, location,
    required: required.map(({ email, name }) => ({ email, name })), optional: optional.map(({ email, name }) => ({ email, name })), rooms,
    showAs, reminderSet, isPrivate, rule, body: body ?? editing?.bodyHtml ?? '', chosenCategories, onlineUrl });
  const [initialSnapshot] = useState(snapshot);
  const dirty = snapshot !== initialSnapshot || attachments.dirty;
  useReportEdits(dirty, saving);
  const confirmation = useMemo(() => ({ title: t('webmail.calendar.editor.discardTitle'), description: t('webmail.calendar.editor.discardDescription'), confirmLabel: t('wizard.discard') }), [t]);
  const { request: requestDiscard, prompt: discardPrompt } = useDiscardGuard({ dirty, busy: saving, confirmation });
  const blocker = useBlocker(dirty || saving);
  useEffect(() => {
    if (blocker.state === 'blocked' && !saving) requestDiscard(() => blocker.proceed(), () => blocker.reset());
  }, [blocker, saving, requestDiscard]);
  const locationSuggestions = useMemo(() => [...new Set([...recentLocations(), ...(roomList.data ?? []).map((r) => r.name)])], [roomList.data]);
  // Conflict warning: other busy events of the own calendar in this time (Outlook "Conflicts with …").
  const conflictsOf = useAsync(
    () => (valid ? api.listEvents(['own'], display.fromWall(start).toISOString(), display.fromWall(end).toISOString()) : Promise.resolve([])),
    `${valid}|${start.getTime()}|${end.getTime()}`,
  );
  const conflicts = (conflictsOf.data ?? [])
    .flatMap((r) => r.events)
    .filter((e) => e.id !== editing?.id && (e.seriesId === null || e.seriesId !== editing?.seriesId) && e.showAs !== 'free' && e.myResponse !== 'declined' && !e.allDay)
    .map((e) => e.subject || t('webmail.calendar.noTitle'));

  // Free/busy for the scheduling strip: me, attendees, rooms on the start day.
  const addresses = useMemo(
    () => [...new Set([session?.username ?? '', ...required.map((r) => r.email), ...optional.map((r) => r.email), ...rooms.map((r) => r.address)].filter((a) => a.includes('@')))],
    [session?.username, required, optional, rooms],
  );
  const day = startOfDay(Number.isNaN(start.getTime()) ? display.toWall(new Date()) : start);
  const freeBusy = useAsync(
    () => (addresses.length > 1 ? api.freeBusy(addresses, display.fromWall(day).toISOString(), display.fromWall(addDays(day, 1)).toISOString()) : Promise.resolve(undefined)),
    `${addresses.join(',')}|${day.getTime()}`,
  );


  const nameOf = (address: string) =>
    address === session?.username ? t('webmail.calendar.editor.you') : ([...required, ...optional].find((r) => r.email === address)?.name ?? rooms.find((r) => r.address === address)?.name ?? address);
  const rows: SchedulingRow[] = (freeBusy.data?.attendees ?? []).map((a) => ({
    id: a.address,
    label: nameOf(a.address),
    detail: rooms.some((r) => r.address === a.address) ? t(roomList.data?.find((r) => r.address === a.address)?.kind === 'equipment' ? 'webmail.calendar.add.equipment' : 'webmail.calendar.editor.room') : undefined,
    busy: a.busy.map((b) => ({ start: display.toWall(b.start), end: display.toWall(b.end), kind: b.showAs })),
    unavailable: a.error ? t('webmail.calendar.editor.noFreeBusy') : undefined,
  }));

  const setStart = (date: string, time: string) => {
    const before = combine(startDate, startTime);
    const after = combine(date, time);
    setStartDate(date);
    setStartTime(time);
    // Keep the duration (Outlook moves the end with the start).
    if (!Number.isNaN(after.getTime()) && !Number.isNaN(before.getTime())) {
      const moved = new Date(end.getTime() + (after.getTime() - before.getTime()));
      setEndDate(dateValue(allDay ? addDays(moved, -1) : moved));
      setEndTime(timeValue(moved));
    }
  };

  const addRoom = (room: Room) => {
    if (rooms.some((r) => r.address === room.address)) return;
    setRooms([...rooms, { name: room.name, address: room.address }]);
    if (!location && room.kind === 'room') setLocation(room.name);
  };

  const save = async () => {
    setError(undefined);
    if (!valid) {
      setError(t(subject.trim() === '' ? 'webmail.calendar.editor.titleRequired' : 'webmail.calendar.editor.endBeforeStart'));
      return;
    }
    if (onlineUrl.trim() !== '' && !validUrl(onlineUrl.trim())) {
      setError(t('webmail.calendarUx.editor.meetingLinkInvalid'));
      return;
    }
    setSaving(true);
    try {
      const [checkedRequired, checkedOptional] = await Promise.all([checkNames(required, directory), checkNames(optional, directory)]);
      setRequired(checkedRequired);
      setOptional(checkedOptional);
      if ([...checkedRequired, ...checkedOptional].some(needsAttention)) {
        setError(t('webmail.calendar.editor.checkAttendees'));
        return;
      }
      const attendees = [
        ...checkedRequired.map((r) => ({ address: r.email, ...(r.name && { name: r.name }), type: 'required' as const })),
        ...checkedOptional.map((r) => ({ address: r.email, ...(r.name && { name: r.name }), type: 'optional' as const })),
        ...rooms.map((r) => ({ address: r.address, name: r.name, type: 'resource' as const })),
      ];
      const recurrence = canRepeat && !readOnlyRule ? (rule ? anchored(rule, start) : null) : undefined;
      const input: CalendarEventInput = {
        subject: subject.trim(),
        start: display.fromWall(start).toISOString(),
        end: display.fromWall(end).toISOString(),
        allDay,
        location: location.trim(),
        showAs,
        sensitivity: isPrivate ? 'private' : 'normal',
        reminderMinutes: reminder,
        ...((editing || reminderSet.length > 1) && { reminders: reminderSet.slice(1) }),
        attendees,
        ...(body !== undefined && { bodyHtml: body }),
        ...((editing || chosenCategories.length > 0) && { categories: chosenCategories }),
        ...((editing ? onlineUrl.trim() !== (editing.onlineMeetingUrl ?? '') : onlineUrl.trim() !== '') && { onlineMeetingUrl: onlineUrl.trim() || null }),
        ...(recurrence !== undefined && (recurrence !== null || editing?.isRecurring) && { recurrence }),
        // Always the zone the user plans in, so Outlook and phones show the series in it (not UTC).
        timeZone: display.zone,
      };
      const saved = editing ? await (scope === 'following' ? api.splitSeries(editing.id, input) : api.updateEvent(editing.id, input, scope)) : await api.createEvent(calendarId, input);
      await attachments.save(saved.id);
      rememberLocation(location.trim());
      toast.success(t(attendees.length > 0 ? (editing ? 'webmail.calendar.editor.updatesSentToast' : 'webmail.calendar.editor.invitationsSentToast') : 'webmail.calendar.editor.savedToast'));
      changed();
      closeEditor();
    } catch (caught) {
      setError(errorText.describe(caught));
    } finally {
      setSaving(false);
    }
  };

  // Find a time: everybody's free/busy for a week from the start day; required people and rooms must be free.
  const weekBusy = useAsync(
    () => (showScheduling && addresses.length > 1 && valid ? api.schedulingSuggestions({ attendees: addresses, optional: optional.map(r => r.email).filter(email => !required.some(r => r.email.toLowerCase() === email.toLowerCase()) && !rooms.some(r => r.address.toLowerCase() === email.toLowerCase()) && email.toLowerCase() !== session?.username.toLowerCase()), start: display.fromWall(day).toISOString(), end: display.fromWall(addDays(day, 7)).toISOString(), durationMinutes: Math.max(15, Math.round((end.getTime() - start.getTime()) / 60_000) || 30) }) : Promise.resolve(undefined)),
    `${showScheduling}|${addresses.join(',')}|${optional.map(r => r.email).join(',')}|${start.getTime()}|${end.getTime()}`,
  );
  const suggestions = weekBusy.loading || weekBusy.error ? [] : (weekBusy.data?.suggestions ?? []).map(s => ({ ...s, start: new Date(s.start), end: new Date(s.end) }));
  const roomPicker = <><Button variant="outline" disabled={!slotValid} onClick={() => setRoomFinderOpen(true)}><DoorOpenIcon />{t('webmail.roomFinder.title')}</Button>{roomFinderOpen && slotValid && <RoomFinder start={display.fromWall(start).toISOString()} end={display.fromWall(end).toISOString()} timeZone={display.zone} recurrence={rule ? anchored(rule, start) : null} selected={rooms.map(r => r.address)} add={addRoom} close={() => setRoomFinderOpen(false)} />}</>;
  const roomChips = rooms.length > 0 && (
  <ul className="flex flex-wrap gap-1.5" aria-label={t('webmail.calendar.rooms')}>
    {rooms.map((room) => (
      <li key={room.address}>
        <Badge variant="secondary" className="gap-1 pr-0.5">
          {room.name}
          <Button variant="ghost" size="icon-sm" className="size-5" aria-label={t('webmail.calendar.editor.removeRoom', { name: room.name })} onClick={() => setRooms(rooms.filter((r) => r !== room))}>
            <XIcon />
          </Button>
        </Badge>
      </li>
    ))}
  </ul>
  );
  const meeting = required.length + optional.length + rooms.length > 0;
  const toggle = (on: boolean) => cn(on && 'bg-accent text-accent-foreground');
  const remove = () => {
    if (!editing) return;
    closeEditor();
    // Meetings are cancelled/declined (attendees told) after the Undo time like every delete (undo.ts).
    undoable.remove(editing, { scope: scope === 'series' ? 'series' : 'occurrence', title: editing.subject });
  };

  const bar = (
    <div role="toolbar" aria-label={t('webmail.calendarUx.editor.toolbar')} className="flex flex-wrap items-center gap-1 border-b pb-2">
      <Button size="sm" onClick={() => void save()} disabled={saving}>
        {meeting ? <SendIcon /> : <SaveIcon />}
        {t(meeting ? (editing ? 'webmail.calendar.editor.sendUpdate' : 'webmail.calendar.editor.send') : 'common.save')}
      </Button>
      {isDesktop && editing?.canDelete && (
        <Button size="sm" variant="ghost" onClick={remove} disabled={saving}>
          <Trash2Icon />
          {t(editing.isMeeting && editing.isOrganizer ? 'webmail.calendar.cancelMeeting' : 'webmail.calendar.delete')}
        </Button>
      )}
      <span aria-hidden className="mx-1 h-5 w-px bg-border" />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" aria-label={t('webmail.calendarUx.editor.showAsValue', { value: t(SHOW_AS_LABEL[showAs]) })}>
            <CalendarCheckIcon />
            <span className="max-md:hidden">{t(SHOW_AS_LABEL[showAs])}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{t('webmail.calendar.showAs.label')}</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={showAs} onValueChange={(v) => setShowAs(v as (typeof SHOW_AS)[number])}>
            {SHOW_AS.map((s) => (
              <DropdownMenuRadioItem key={s} value={s}>
                {t(SHOW_AS_LABEL[s])}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="ghost" aria-label={t('webmail.calendarUx.editor.reminderValue', { value: remindersLabel })}>
            {reminder === null ? <BellOffIcon /> : <BellIcon />}
            <span className="max-md:hidden">{remindersLabel}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>{t('webmail.calendar.reminder')}</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => setReminderSet([])}>{reminderLabel(null)}</DropdownMenuItem>
          {REMINDERS.filter((m): m is Exclude<(typeof REMINDERS)[number], null> => m !== null).map((m) => (
            <DropdownMenuCheckboxItem
              key={m}
              checked={reminderSet.includes(m)}
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={(on) => setReminderSet(on ? [...reminderSet, m].sort((a, b) => a - b).slice(0, 6) : reminderSet.filter((x) => x !== m))}
            >
              {reminderLabel(m)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {isDesktop && canRepeat && (
        <Button size="sm" variant="ghost" aria-pressed={showRepeat} className={toggle(showRepeat || rule !== null)} onClick={() => setShowRepeat(!showRepeat)}>
          <RepeatIcon />
          <span className="max-md:hidden">{t('webmail.calendar.editor.repeat')}</span>
        </Button>
      )}
      {isDesktop && <Button size="icon-sm" variant="ghost" aria-pressed={isPrivate} aria-label={t('webmail.calendar.private')} title={t('webmail.calendar.private')} className={toggle(isPrivate)} onClick={() => setPrivate(!isPrivate)}>
        <LockIcon />
      </Button>}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon-sm" variant="ghost" aria-label={t('webmail.calendarUx.editor.categories')} title={t('webmail.calendarUx.editor.categories')} className={toggle(chosenCategories.length > 0)}>
            <TagIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel>{t('webmail.calendarUx.editor.categories')}</DropdownMenuLabel>
          {categories.length === 0 && <DropdownMenuItem disabled>{t('webmail.calendarUx.editor.noCategories')}</DropdownMenuItem>}
          {categories.map((c) => (
            <DropdownMenuCheckboxItem
              key={c.name}
              checked={chosenCategories.some((n) => n.toLowerCase() === c.name.toLowerCase())}
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={(on) => setChosenCategories(on ? [...chosenCategories, c.name] : chosenCategories.filter((n) => n.toLowerCase() !== c.name.toLowerCase()))}
            >
              <span aria-hidden className={cn('size-2.5 rounded-full', CALENDAR_COLOR_CLASSES[c.color].swatch)} />
              {c.name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {isDesktop && <Button size="icon-sm" variant="ghost" aria-pressed={showOnline} aria-label={t('webmail.calendarUx.editor.onlineMeeting')} title={t('webmail.calendarUx.editor.onlineMeeting')} className={toggle(showOnline || onlineUrl !== '')} onClick={() => setShowOnline(!showOnline)}>
        <VideoIcon />
      </Button>}
      {isDesktop && <Button size="icon-sm" variant="ghost" aria-label={t('webmail.calendarUx.editor.attach')} title={t('webmail.calendarUx.editor.attach')} onClick={attachments.pick}>
        <PaperclipIcon />
      </Button>}
      {isDesktop && <Button size="sm" variant="ghost" aria-pressed={showScheduling} className={toggle(showScheduling)} onClick={() => setShowScheduling(!showScheduling)}>
        <CalendarRangeIcon />
        <span className="max-md:hidden">{t('webmail.calendarUx.editor.scheduling')}</span>
      </Button>}
      {!isDesktop && <EventSecondaryMenu disabled={saving} canRepeat={canRepeat} repeatVisible={showRepeat} privateEvent={isPrivate}
        onlineVisible={showOnline} schedulingVisible={showScheduling} onToggleRepeat={() => setShowRepeat(!showRepeat)}
        onTogglePrivate={() => setPrivate(!isPrivate)} onToggleOnline={() => setShowOnline(!showOnline)} onAttach={attachments.pick}
        onToggleScheduling={() => setShowScheduling(!showScheduling)}
        {...(editing?.canDelete ? { onDelete: remove, deleteLabel: editing.isMeeting && editing.isOrganizer ? 'webmail.calendar.cancelMeeting' as const : 'webmail.calendar.delete' as const } : {})} />}
    </div>
  );

  return (
    <><Dialog open onOpenChange={(open) => { if (!open) requestDiscard(closeEditor); }}>
      <DialogContent framed size="xl" initialFocusRef={titleInput} closeDisabled={saving} className="h-full gap-3 md:h-5/6">
        <DialogHeader className="sr-only">
          <DialogTitle>{t(editing ? 'webmail.calendar.editor.editTitle' : meeting ? 'webmail.calendar.editor.newMeeting' : 'webmail.calendar.editor.newTitle')}</DialogTitle>
          <DialogDescription>{t(editing && scope === 'series' ? 'webmail.calendar.editor.seriesNote' : 'webmail.calendar.editor.description')}</DialogDescription>
        </DialogHeader>
        <fieldset disabled={saving} aria-busy={saving} className="contents">
        {bar}
        {/* Native fields are disabled; inert also freezes contenteditable and composite editor controls. */}
        <DialogBody inert={saving} className="flex flex-col gap-3 pr-1">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              id={ids.title}
              ref={titleInput}
              value={subject}
              placeholder={t('webmail.calendarUx.editor.titlePlaceholder')}
              aria-label={t('webmail.calendar.editor.title')}
              className="h-10 min-w-0 flex-1 text-base font-medium"
              onChange={(e) => setSubject(e.target.value)}
              aria-invalid={error !== undefined && subject.trim() === ''}
              aria-describedby={error ? ids.error : undefined}
            />
            {!editing && writable.length > 1 && (
              <EntityListPicker<string> label={t('webmail.calendar.editor.calendar')} value={calendarId} items={writable.map((c) => ({ id: c.id, label: calendarName(c, t), identity: calendarName(c, t) }))} labels={entityPickerLabelsFor(t)} onChange={(entry) => { if (entry) setCalendarId(entry.id); }} />
            )}
          </div>
          <div className="flex items-start gap-2">
            <UsersIcon aria-hidden className="mt-2 size-4 shrink-0 text-muted-foreground" />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <RecipientInput id={ids.required} field="required" label={t('webmail.calendar.editor.required')} value={required} onChange={setRequired} directory={directory} labels={recipientLabels} placeholder={t('webmail.calendarUx.editor.attendeesPlaceholder')} />
              {showOptional || optional.length > 0 ? (
                <RecipientInput id={ids.optional} field="optional" label={t('webmail.calendar.editor.optional')} value={optional} onChange={setOptional} directory={directory} labels={recipientLabels} placeholder={t('webmail.calendar.editor.optional')} />
              ) : (
                <Button variant="link" size="sm" className="h-auto self-start p-0" onClick={() => setShowOptional(true)}>
                  {t('webmail.calendarUx.editor.addOptional')}
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ClockIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
            <DateTimePicker id={ids.start} mode="date" weekStartsOn={format.weekStart} weekTimeZone={format.weekTimeZone} className="w-48" aria-label={t('webmail.calendar.editor.startDate')} value={startDate} onValueChange={(next) => setStart(next, startTime)} />
            {!allDay && <DateTimePicker mode="time" formatTime={format.time} className="w-36" aria-label={t('webmail.calendar.editor.startTime')} value={startTime} step={300} onValueChange={(next) => setStart(startDate, next)} />}
            <span aria-hidden>–</span>
            {!allDay && <DateTimePicker mode="time" formatTime={format.time} className="w-36" aria-label={t('webmail.calendar.editor.endTime')} value={endTime} step={300} onValueChange={setEndTime} />}
            <DateTimePicker id={ids.end} mode="date" weekStartsOn={format.weekStart} weekTimeZone={format.weekTimeZone} className="w-48" aria-label={t('webmail.calendar.editor.endDate')} value={endDate} onValueChange={setEndDate} />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={allDay} onCheckedChange={(v) => setAllDay(v === true)} />
              {t('webmail.calendar.allDay')}
            </label>
            {end > start && <span className="text-xs text-muted-foreground">{format.duration(start, end, allDay)}</span>}
          </div>
          {canRepeat && (showRepeat || rule !== null) && <RecurrenceEditor value={rule} onChange={setRule} start={Number.isNaN(start.getTime()) ? initialStart : start} disabled={readOnlyRule} />}
          <div className="flex items-start gap-2">
            <MapPinIcon aria-hidden className="mt-2 size-4 shrink-0 text-muted-foreground" />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <div className="flex gap-2">
                <Input id={ids.location} list={ids.locations} value={location} aria-label={t('webmail.calendar.location')} placeholder={t('webmail.calendarUx.editor.locationPlaceholder')} onChange={(e) => setLocation(e.target.value)} />
                <datalist id={ids.locations}>
                  {locationSuggestions.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
                {roomPicker}
              </div>
              {roomChips}
              {conflicts.length > 0 && (
                <p role="status" className="flex items-center gap-1.5 text-xs text-warning">
                  <TriangleAlertIcon aria-hidden className="size-3.5" />
                  {t('webmail.calendarUx.editor.conflicts', { n: conflicts.length, titles: format.list(conflicts.slice(0, 3)) })}
                </p>
              )}
              {attachments.view}
            </div>
          </div>
          {(showOnline || onlineUrl !== '') && (
            <div className="flex items-center gap-2">
              <VideoIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              <Input type="url" value={onlineUrl} aria-label={t('webmail.calendarUx.editor.meetingLink')} placeholder={t('webmail.calendarUx.editor.meetingLinkPlaceholder')} onChange={(e) => setOnlineUrl(e.target.value)} aria-invalid={onlineUrl !== '' && !validUrl(onlineUrl)} />
            </div>
          )}
          {showScheduling && addresses.length > 1 && (
            <section aria-label={t('webmail.calendarUx.findTime.title')} className="flex flex-col gap-1.5">
              <h3 className="text-sm font-medium">{t('webmail.calendarUx.findTime.title')}</h3>
              {weekBusy.loading && !weekBusy.data ? (
                <p className="text-xs text-muted-foreground">{t('webmail.calendarUx.findTime.loading')}</p>
              ) : suggestions.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('webmail.calendarUx.findTime.none')}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s) => {
                    const wall = { start: display.toWall(s.start), end: display.toWall(s.end) };
                    return (
                      <Button
                        key={s.start.getTime()}
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setStartDate(dateValue(wall.start));
                          setStartTime(timeValue(wall.start));
                          setEndDate(dateValue(wall.end));
                          setEndTime(timeValue(wall.end));
                        }}
                      >
                        {format.shortDay(wall.start)} {format.range(wall.start, wall.end, false)}
                        {s.optionalBusy > 0 && <Badge variant="muted">{t('webmail.calendarUx.findTime.optionalBusy', { n: s.optionalBusy })}</Badge>}
                      </Button>
                    );
                  })}
                </div>
              )}
            </section>
          )}
          {showScheduling && (
            <SchedulingStrip
              day={day}
              rows={rows}
              slot={{ start, end }}
              loading={freeBusy.loading}
              formatHour={format.hour}
              rowSummary={(row) =>
                row.unavailable
                  ? t('webmail.calendar.editor.rowUnavailable', { name: row.label })
                  : t('webmail.calendar.editor.rowSummary', { name: row.label, n: row.busy.filter((b) => b.start < end && b.end > start).length })
              }
              labels={{
                title: t('webmail.calendar.editor.availability'),
                legendBusy: t('webmail.calendar.showAs.busy'),
                legendTentative: t('webmail.calendar.showAs.tentative'),
                legendAway: t('webmail.calendar.showAs.oof'),
                slot: t('webmail.calendar.editor.thisMeeting'),
              }}
            />
          )}
          <RichTextEditor
            compactToolbar
            label={t('webmail.calendar.description')}
            placeholder={t('webmail.calendar.editor.descriptionPlaceholder')}
            defaultValue={editing ? { format: 'html', content: editing.bodyHtml } : undefined}
            onChange={(value) => setBody(value.format === 'html' ? value.content : plainTextToMailHtml(value.content))}
            className="min-h-48 flex-1"
          />
        </DialogBody>
        </fieldset>
        {error && (
          <p id={ids.error} role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </DialogContent>
    </Dialog>{discardPrompt}</>
  );
}
